import React from 'react';
import { useIntl } from 'react-intl';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import type { MailboxGuideStep } from 'fym-shared/src/music24/outreach/mailboxProviders';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import type { MailboxGuideStepCopy } from './mailboxGuideCopy';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the guide; each part uses its own slice of it */
import styles from './MailboxGuide.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface GuideStepCardProps {
  number: number;
  step: MailboxGuideStep;
  copy: MailboxGuideStepCopy | undefined;
  state: 'done' | 'now' | 'todo';
  /** A link of the provider was opened: the step counts as done. */
  onOpened: (key: string) => void;
  /** A step with a label but no URL opens a screen of the guide ("Use another mailbox"). */
  onOwnLink: (key: string) => void;
  children?: React.ReactNode;
}

/** One of the 3 steps of a provider guide (design outreach-v2 o2 `g-st`). Links open in a new tab. */
const GuideStepCardComponent: React.FC<GuideStepCardProps> = ({
  number,
  step,
  copy,
  state,
  onOpened,
  onOwnLink,
  children,
}) => {
  const intl = useIntl();
  const linkLabel = copy?.link === undefined ? step.linkLabel : intl.formatMessage(copy.link);
  return (
    <div
      className={clsx(styles.step, state === 'done' && styles.stepDone, state === 'now' && styles.stepNow)}
      data-testid={`guide-step-${step.key}`}
    >
      <span className={styles.number}>{state === 'done' ? '✓' : number}</span>
      <div>
        <b>{copy === undefined ? step.title : intl.formatMessage(copy.title)}</b>
        <p>{copy === undefined ? step.body : intl.formatMessage(copy.body)}</p>
        {linkLabel !== undefined && (
          <div className={styles.actions}>
            {step.linkUrl !== undefined ? (
              <a
                className={clsx('btn btn-sm', state === 'now' ? 'btn-primary' : 'btn-outline-secondary')}
                href={step.linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onOpened(step.key)}
              >
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="me-2" />
                {linkLabel}
              </a>
            ) : (
              <SMLButton size="sm" outline omitCap onClick={() => onOwnLink(step.key)}>
                {linkLabel}
              </SMLButton>
            )}
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

export const GuideStepCard = TypedMemo(GuideStepCardComponent);
